// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { TextField } from '@fluentui/react';
import React, { Fragment, useState } from 'react';
import { inputBoxStyle, inputBoxTextStyle, TextFieldStyleProps } from './styles/DisplayField.styles';

interface DisplayNameFieldProps {
  setErrorMsg(errorMsg: string): void;
  setValue(displayValue: string): void;
  setEmptyWarning?(isEmpty: boolean): void;
  isEmpty?: boolean;
  defaultValue?: string;
  errorMsg?: string;
  DISPLAY_NAME_MAX_CHARS?: number;
  TEXTFIELD_LABEL?: string;
  TEXTFIELD_ID?: string;
  TEXTFIELD_PLACEHOLDER?: string;
  TEXTFIELD_EMPTY_ERROR_MSG?: string;
  TEXTFIELD_EXCEEDS_MAX_CHARS?: string;
  type?: string;
  required?: boolean;
}

const hasValidLength = (name: string, maxchar: number): boolean => {
  return name.length <= maxchar;
};

export const DisplayTextField = (props: DisplayNameFieldProps): JSX.Element => {
  const { setValue, setEmptyWarning, isEmpty,
    defaultValue,
    setErrorMsg,
    errorMsg,
    DISPLAY_NAME_MAX_CHARS,
    TEXTFIELD_LABEL,
    TEXTFIELD_ID,
    TEXTFIELD_PLACEHOLDER,
    TEXTFIELD_EMPTY_ERROR_MSG,
    type,
    required
  } = props;
  const TEXTFIELD_EXCEEDS_MAX_CHARS = `${TEXTFIELD_LABEL} cannot exceed ${DISPLAY_NAME_MAX_CHARS} characters`;
  const [isInvalidLength, setIsInvalidLength] = useState<boolean>(!hasValidLength(defaultValue ?? '', DISPLAY_NAME_MAX_CHARS ?? 0));

  const onValueChange = (e?: any): void => {
    const name = e.target.id;
    const newValue = e.target.value;
    if (newValue === undefined) {
      return;
    }
    if (name === 'displayIMONumber' || name === 'displayHEXID' || name === 'displayMMSINumber') {
      setValue(newValue);
    } else {
      const errorMsg: any = validateField(name, newValue);
      setErrorMsg(errorMsg);
      if (!hasValidLength(newValue, DISPLAY_NAME_MAX_CHARS ?? 0)) {
        setIsInvalidLength(true);
        setValue('');
        return;
      } else {
        setIsInvalidLength(false);
      }

      setValue(newValue);
      if (setEmptyWarning && !newValue) {
        setEmptyWarning(true);
      } else {
        setEmptyWarning && setEmptyWarning(false);
      }
    }
  };

  const validateNumber = (evt: any) => {
    var theEvent = evt || window.event;

    // Handle paste
    if (theEvent.type === "paste") {
      key = theEvent.clipboardData.getData("text/plain");
    } else {
      // Handle key press
      var key = theEvent.keyCode || theEvent.which;
      key = String.fromCharCode(key);
    }
    var regex = /[0-9]|\./;
    if (!regex.test(key)) {
      theEvent.returnValue = false;
      if (theEvent.preventDefault) theEvent.preventDefault();
    }
  };

  const validateField = (name: any, value: any) => {
    let errorMsg = null;
    switch (name) {
      case "displayEmail":
        if (!value) errorMsg = "Please enter Email.";
        else if (!/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value))
          errorMsg = "Please enter valid Email.";
        break;
      case "displayMobile":
        if (!value) errorMsg = "Please enter Mobile Number.";
        break;
      case "displayFirstName":
        if (!value) errorMsg = "Please enter first name.";
        break;
      case "displayLastName":
        if (!value) errorMsg = "Please enter last name.";
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const customrender = (label?: string): any => {
    return (
      <Fragment>
        {label}<span style={{ marginLeft: '5px', fontSize: '12px', fontStyle: 'italic', color: '#808080' }}>(Optional)</span>
      </Fragment>
    )
  }

  if (type === 'email') {
    return (
      <TextField
        autoComplete="off"
        defaultValue={defaultValue}
        inputClassName={inputBoxTextStyle}
        label={TEXTFIELD_LABEL}
        required={required}
        className={inputBoxStyle}
        onChange={(e) => onValueChange(e)}
        id={TEXTFIELD_ID}
        placeholder={TEXTFIELD_PLACEHOLDER}
        styles={TextFieldStyleProps}
        errorMessage={(errorMsg ? errorMsg : '') || (isEmpty ? TEXTFIELD_EMPTY_ERROR_MSG : isInvalidLength ? TEXTFIELD_EXCEEDS_MAX_CHARS : undefined)}
      />
    );
  } else if (type === 'number') {
    return (
      <TextField
        autoComplete="off"
        defaultValue={defaultValue}
        inputClassName={inputBoxTextStyle}
        label={TEXTFIELD_LABEL}
        required={required}
        className={inputBoxStyle}
        onChange={(e) => onValueChange(e)}
        onKeyPress={validateNumber}
        id={TEXTFIELD_ID}
        placeholder={TEXTFIELD_PLACEHOLDER}
        styles={TextFieldStyleProps}
        errorMessage={(errorMsg ? errorMsg : '') || (isEmpty ? TEXTFIELD_EMPTY_ERROR_MSG : isInvalidLength ? TEXTFIELD_EXCEEDS_MAX_CHARS : undefined)}
      />
    );
  } else if (type === 'text') {
    return (
      <TextField
        autoComplete="off"
        defaultValue={defaultValue}
        inputClassName={inputBoxTextStyle}
        label={TEXTFIELD_LABEL}
        required={required}
        className={inputBoxStyle}
        onChange={(e) => onValueChange(e)}
        id={TEXTFIELD_ID}
        placeholder={TEXTFIELD_PLACEHOLDER}
        styles={TextFieldStyleProps}
        errorMessage={(errorMsg ? errorMsg : '') || (isEmpty ? TEXTFIELD_EMPTY_ERROR_MSG : isInvalidLength ? TEXTFIELD_EXCEEDS_MAX_CHARS : undefined)}
      />
    );
  } else if (type === 'textArea') {
    return (
      <TextField
        autoComplete="off"
        defaultValue={defaultValue}
        inputClassName={inputBoxTextStyle}
        label={TEXTFIELD_LABEL}
        required={required}
        className={inputBoxStyle}
        onChange={(e) => onValueChange(e)}
        id={TEXTFIELD_ID}
        placeholder={TEXTFIELD_PLACEHOLDER}
        styles={TextFieldStyleProps}
        multiline rows={5}
        errorMessage={(errorMsg ? errorMsg : '') || (isEmpty ? TEXTFIELD_EMPTY_ERROR_MSG : isInvalidLength ? TEXTFIELD_EXCEEDS_MAX_CHARS : undefined)}
      />
    );
  } else {
    return (
      <TextField
        autoComplete="off"
        defaultValue={defaultValue}
        inputClassName={inputBoxTextStyle}
        label={customrender(TEXTFIELD_LABEL)}
        required={required}
        className={inputBoxStyle}
        onChange={(e) => onValueChange(e)}
        id={TEXTFIELD_ID}
        placeholder={TEXTFIELD_PLACEHOLDER}
        styles={TextFieldStyleProps}
      />
    );
  }
};
